import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStore } from "../contexts/StoreContext";
import {
  fetchMessages,
  fetchPasses,
  fetchIntegrations,
  fetchtTwilioPhoneNumbers,
  fetchIntegrationsEmailAddresses,
} from "../api/queries";
import { deleteMessage, createMessage, updateMessage } from "../api/mutations";
import { getMarketingTimeZone } from "../utils/marketingTimeZone";
import { utcIsoToZonedWallTime } from "../utils/zonedTime";
import { providersFor } from "../pages/marketing/messageUtils";

export const initialFormState = {
  pass_id: "all",
  message_type: "push",
  scheduled_at: "",
  message: "",
  from: "",
  subject: "",
  integration_id: "",
  // Dashboard segment (CustomerSegments id) for a push; "" = no segment.
  segment: "",
  // UI only, never sent: "now" posts scheduled_at null (the API sends at
  // once), "later" posts the entered time.
  when: "now",
};

/**
 * State, data and CRUD for the Marketing page: the message list, the
 * composer's form, the message being viewed and the one being deleted, and
 * the time zone message times are entered and shown in.
 */
export const useMarketingData = () => {
  const { selectedStore } = useStore();
  const queryClient = useQueryClient();
  const storeId = selectedStore?.id;
  const storeTimeZone = selectedStore?.time_zone;

  // Resolved once here and passed down, so the list, the view drawer, the
  // edit pre-fill and the composer's submit always use the same zone.
  const { timeZone, fromStore: timeZoneFromStore } = useMemo(
    () => getMarketingTimeZone({ time_zone: storeTimeZone }),
    [storeTimeZone],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  // Only the id: the drawer shows the message as it is in the current list,
  // so a refetch (sent, failed) reaches it.
  const [viewingMessageId, setViewingMessageId] = useState(null);
  const [deletingMessage, setDeletingMessage] = useState(null);
  const [formData, setFormData] = useState(initialFormState);

  const { data: messages = [], isLoading: isLoadingMessages } = useQuery({
    queryKey: ["messages", storeId],
    queryFn: () => fetchMessages(storeId),
    enabled: !!storeId,
  });

  const { data: passes = [] } = useQuery({
    queryKey: ["passes", storeId],
    queryFn: () => fetchPasses(storeId),
    enabled: !!storeId,
  });

  const viewingMessage =
    viewingMessageId === null
      ? null
      : messages.find((message) => message.id === viewingMessageId) || null;
  // Gone from the list (deleted elsewhere): close the drawer for good, so the
  // id does not reopen it later.
  if (viewingMessageId !== null && !viewingMessage) setViewingMessageId(null);

  const { data: integrationsData, isLoading: isLoadingIntegrations } = useQuery(
    {
      queryKey: ["integrations", storeId],
      queryFn: () => fetchIntegrations(storeId),
      enabled: !!storeId,
    },
  );
  const integrations = useMemo(
    () => integrationsData || [],
    [integrationsData],
  );

  const { data: phoneNumbers = {}, isLoading: isLoadingPhoneNumbers } =
    useQuery({
      queryKey: ["twilioPhoneNumbers", storeId, formData.integration_id],
      queryFn: () => fetchtTwilioPhoneNumbers(storeId, formData.integration_id),
      enabled:
        isModalOpen &&
        !!storeId &&
        !!formData.integration_id &&
        formData.message_type === "sms",
    });

  const { data: emailAddresses = {}, isLoading: isLoadingEmailAddresses } =
    useQuery({
      queryKey: [
        "integrationsEmailAddresses",
        storeId,
        formData.integration_id,
      ],
      queryFn: () =>
        fetchIntegrationsEmailAddresses(storeId, formData.integration_id),
      enabled:
        isModalOpen &&
        !!storeId &&
        !!formData.integration_id &&
        formData.message_type === "email",
    });

  const invalidateMessages = () =>
    queryClient.invalidateQueries({ queryKey: ["messages", storeId] });

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingMessage(null);
  };

  const createMessageMutation = useMutation({
    mutationFn: createMessage,
    onSuccess: () => {
      invalidateMessages();
      handleCloseModal();
    },
  });

  const updateMessageMutation = useMutation({
    mutationFn: updateMessage,
    onSuccess: () => {
      invalidateMessages();
      handleCloseModal();
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: deleteMessage,
    onSuccess: () => {
      invalidateMessages();
      setDeletingMessage(null);
    },
  });

  // `prefill` seeds a new message, e.g. { message_type: "push", segment }
  // when the dashboard's "Create win-back push" opens the form.
  const handleOpenModal = (message = null, prefill = null) => {
    setViewingMessageId(null);
    if (message) {
      setEditingMessage(message);
      // UTC scheduled_at -> wall time in the zone the form submits in, so
      // saving unchanged keeps the same instant.
      const scheduledAt = utcIsoToZonedWallTime(message.scheduled_at, timeZone);
      setFormData({
        pass_id: message.pass ? message.pass.id.toString() : "all",
        message_type: message.message_type || "push",
        scheduled_at: scheduledAt,
        message: message.message || "",
        from: message.from || "",
        subject: message.subject || "",
        // The API sends the provider nested (MessageBlueprint has no
        // integration_id), so reading integration_id left it blank on edit.
        integration_id: message.integration?.id
          ? message.integration.id.toString()
          : "",
        segment: message.segment || "",
        when: scheduledAt ? "later" : "now",
      });
    } else {
      setEditingMessage(null);
      setFormData({ ...initialFormState, ...(prefill || {}) });
    }
    createMessageMutation.reset();
    updateMessageMutation.reset();
    setIsModalOpen(true);
  };

  // Switching channel resets what belongs to the old one. A single connected
  // provider is picked for the merchant. Segments are push only (the API
  // answers 422 segment_requires_push otherwise).
  const handleMessageTypeChange = (type) => {
    setFormData((prev) => {
      if (prev.message_type === type) return prev;
      const providers = providersFor(type, integrations);
      return {
        ...prev,
        message_type: type,
        from: "",
        subject: type === "email" ? prev.subject : "",
        integration_id:
          providers.length === 1 ? providers[0].id.toString() : "",
        segment: type === "push" ? prev.segment : "",
      };
    });
  };

  // The same pick when the integrations arrive after the merchant chose email
  // or SMS: a new message on that channel with no provider yet gets the only
  // one. Keyed on the loaded list, so clearing the select is not undone; an
  // edited message keeps the provider it was saved with.
  useEffect(() => {
    if (!isModalOpen || editingMessage || !integrationsData) return;
    setFormData((prev) => {
      if (prev.integration_id) return prev;
      const providers = providersFor(prev.message_type, integrationsData);
      return providers.length === 1
        ? { ...prev, integration_id: providers[0].id.toString() }
        : prev;
    });
  }, [isModalOpen, editingMessage, integrationsData]);

  // `data` is the composer's form with scheduled_at already in UTC (or null
  // for "Send now").
  const handleSubmit = (data) => {
    // eslint-disable-next-line no-unused-vars
    const { when, ...rest } = data;
    const messageData = {
      ...rest,
      integration_id:
        rest.message_type !== "push" && rest.integration_id
          ? parseInt(rest.integration_id, 10)
          : null,
      // Sent as null rather than "" so an edit that removes the segment
      // clears it, and a push only ever carries one.
      segment:
        rest.message_type === "push" && rest.segment ? rest.segment : null,
    };

    if (editingMessage) {
      updateMessageMutation.mutate({
        storeId,
        messageId: editingMessage.id,
        messageData,
      });
    } else {
      createMessageMutation.mutate({ storeId, messageData });
    }
  };

  const handleConfirmDelete = () => {
    if (!deletingMessage) return;
    deleteMessageMutation.mutate({ storeId, messageId: deletingMessage.id });
  };

  return {
    messages,
    passes,
    integrations,
    formData,
    setFormData,
    isModalOpen,
    editingMessage,
    viewingMessage,
    // Takes a message (or null to close); only its id is kept.
    viewMessage: (message) => setViewingMessageId(message?.id ?? null),
    deletingMessage,
    setDeletingMessage,

    isLoadingMessages,
    isLoadingIntegrations,
    isLoadingPhoneNumbers,
    isLoadingEmailAddresses,
    isSaving:
      createMessageMutation.isPending || updateMessageMutation.isPending,
    isDeleting: deleteMessageMutation.isPending,

    phoneNumbers,
    emailAddresses,
    timeZone,
    timeZoneFromStore,

    handleOpenModal,
    handleCloseModal,
    handleMessageTypeChange,
    handleSubmit,
    handleConfirmDelete,
  };
};
